import {
  AfterContentInit,
  Component,
  computed,
  contentChildren,
  input,
  output,
  Signal,
  signal,
  TemplateRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { backNavigation, Command2, MenuItem, menuItems, NavigateBackOptions } from '@softline/application';
import { UiCoreModule } from '@softline/ui-core';
import { WizardStepComponent } from './step/wizard-step.component';
import { WizardItemBase } from './wizard-item-base';
import { WizardActionDirective } from './action/wizard-action.directive';
import { PageComponent } from '../page/page.component';
import { PageHeaderComponent } from '../page/page-header/page-header.component';
import {
  DefaultPageHeaderContentComponent
} from '../../components/default-page-header-content/default-page-header-content.component';

@Component({
    selector: 'soft-wizard',
    imports: [
        CommonModule,
        UiCoreModule,
        PageComponent,
        PageHeaderComponent,
        DefaultPageHeaderContentComponent
    ],
    templateUrl: './wizard.component.html',
    styleUrl: './wizard.component.css'
})
export class WizardComponent
  implements AfterContentInit
{
  running = signal(false);
  index = signal(0);

  headerTemplate = input<TemplateRef<any> | null>(null);

  done = output<void>();
  cancel = output<void>();

  steps = contentChildren(WizardItemBase);

  current: Signal<WizardItemBase | null> = computed(() => {
    const index = this.index();
    return this.steps()[index] ?? null;
  });

  visibleSteps = computed(() => {
    return this.steps().filter((step) => step.state() !== 'hidden');
  });

  visibleIndex = computed(() => {
    let index = this.index();
    const steps = this.steps();
    while (index > -1 && steps[index].state() === 'hidden') index--;
    return index;
  });

  visibleCurrent = computed(() => {
    const index = this.visibleIndex();
    const steps = this.steps();
    return steps[index];
  });

  nextStep: Signal<WizardItemBase | null> = computed(() => {
    const index = this.index();
    const steps = this.steps();
    for (let i = index + 1; index < steps.length; i++)
      if (steps[i]?.state() !== 'disabled') return steps[i] ?? null;
    return null;
  });

  currentTemplate = computed(() => {
    const step = this.visibleCurrent();
    if (step instanceof WizardStepComponent) return step.template();
    return null;
  });

  constructor() {
    backNavigation(async (options?: NavigateBackOptions) => {
      const index = this.index();
      if (index > 0)
        this.index.set(index - 1);
      else this.cancel.emit();
    });
    menuItems(() => {
      const current = this.current();
      const next = this.nextStep();
      const running = this.running();

      let icon = next
        ? (next?.icon() ?? 'fa-regular fa-arrow-right')
        : (current?.icon() ?? 'fa-regular fa-arrow-right')
      if(running)
        icon = 'fa-regular fa-spinner fa-spin';

      const title = next
        ? (running ? current?.title() : next?.title())
        : 'Weiter'

      const visible = next
        ? next?.state() !== 'manual'
        : current?.state() !== 'manual';

      return [
        {
          type: 'execute',
          name: 'StepperPageNextCommand',
          outlet: 'responsive',
          class: 'soft-button gap-3 accent text-nowrap',
          visible,
          icon,
          title,
          canExecute: computed(() => {
            const current = this.current();
            const next = this.nextStep();
            const running = this.running();

            return !running &&
              !!current?.canLeave() &&
              !!next?.canEnter() &&
              next?.state() !== 'disabled'
          }),
          execute: async () => this.next()
        },
    ];
    })
  }

  ngAfterContentInit() {
    const current = this.current();
    if (current) current.enter.emit(current);
  }

  private async runAction(action: WizardActionDirective): Promise<void> {
    try {
      this.running.set(true);
      const proceed = await new Promise<boolean>((resolve, reject) => {
        const callback = (proceed: boolean) => {
          resolve(proceed);
        };

        action.execute.emit(callback);
      });

      let index: number;
      const nextStep = this.nextStep();
      if (proceed && nextStep) index = this.steps().indexOf(nextStep);
      else {
        index = this.index();
        const steps = this.steps();
        for (let i = index - 1; index >= 0; i--)
          if (steps[i].state() !== 'disabled') break;
      }
      if (index > -1) this.index.set(index);
    } catch (e) {
      console.error(e);
    } finally {
      this.running.set(false);
    }
  }

  public async next(): Promise<void> {
    const current = this.current();
    const next = this.nextStep();

    if (next) {
      const nextIndex = this.steps().indexOf(next);
      this.index.set(nextIndex);
      current?.next.emit({ current, next });
      current?.leave.emit(current);
      next.enter.emit(next);

      if (next instanceof WizardActionDirective) await this.runAction(next);
    } else {
      this.done.emit();
    }
  }
}
